/**
 * Decides what the upload will actually do, before anything is written.
 *
 * address-book-api only rejects an exact duplicate (same reference type AND value)
 * and stores a differing value as a non-canonical alias. We want one reference per
 * company, so the rule is enforced here instead: a company that already holds a
 * reference of the chosen type is skipped whatever its value.
 *
 * The consequence, which the page states plainly: this fills blanks and never
 * corrects. Changing a stored value means deleting the old one first.
 */

import type { ParsedRow } from "./parseCsv.js";

export type RowPlan = "write" | "already-mapped" | "duplicate-in-file" | "malformed";

export type PlannedRow = ParsedRow & {
  plan: RowPlan;
  /** What the company already holds, when the row is skipped as already-mapped. */
  existingValue: string | null;
};

export type PlanCounts = {
  write: number;
  alreadyMapped: number;
  duplicateInFile: number;
  malformed: number;
};

export type UploadPlan = {
  rows: PlannedRow[];
  counts: PlanCounts;
  /** Companies appearing more than once in the file, for the preview warning. */
  repeatedCompanies: Array<{ companyUuid: string; rows: PlannedRow[] }>;
};

/** UUIDs may arrive in either case, so every comparison goes through this. */
const key = (companyUuid: string): string => companyUuid.toLowerCase();

/**
 * @param rows Parsed rows, in file order — order decides which row wins for a
 *   company that appears more than once.
 * @param existingByCompany Values of the chosen reference type each company already
 *   holds, keyed by company UUID. Callers filter to the relevant type; an absent or
 *   empty entry means the company is unmapped.
 */
export function planUpload(
  rows: ParsedRow[],
  existingByCompany: Map<string, string[]>,
): UploadPlan {
  const existing = new Map<string, string[]>();
  for (const [companyUuid, values] of existingByCompany) {
    existing.set(key(companyUuid), values);
  }

  // Only a row that would otherwise be written claims a company's slot, so a
  // malformed first row does not stop a valid later one from going through.
  const claimed = new Set<string>();

  const planned: PlannedRow[] = rows.map(row => {
    const companyKey = key(row.companyUuid);
    const heldValues = existing.get(companyKey) ?? [];

    if (row.problem) {
      return { ...row, plan: "malformed", existingValue: null };
    }
    if (heldValues.length > 0) {
      return { ...row, plan: "already-mapped", existingValue: heldValues[0] ?? null };
    }
    if (claimed.has(companyKey)) {
      return { ...row, plan: "duplicate-in-file", existingValue: null };
    }

    claimed.add(companyKey);
    return { ...row, plan: "write", existingValue: null };
  });

  const counts: PlanCounts = {
    write: planned.filter(row => row.plan === "write").length,
    alreadyMapped: planned.filter(row => row.plan === "already-mapped").length,
    duplicateInFile: planned.filter(row => row.plan === "duplicate-in-file").length,
    malformed: planned.filter(row => row.plan === "malformed").length,
  };

  const byCompany = new Map<string, PlannedRow[]>();
  for (const row of planned) {
    const companyKey = key(row.companyUuid);
    byCompany.set(companyKey, [...(byCompany.get(companyKey) ?? []), row]);
  }
  const repeatedCompanies = [...byCompany.entries()]
    .filter(([, companyRows]) => companyRows.length > 1)
    .map(([companyUuid, companyRows]) => ({ companyUuid, rows: companyRows }));

  return { rows: planned, counts, repeatedCompanies };
}

/** Companies that need an existing-reference lookup before the plan can be built. */
export function companiesToCheck(rows: ParsedRow[]): string[] {
  const unique = new Set<string>();
  for (const row of rows) {
    if (!row.problem) {
      unique.add(key(row.companyUuid));
    }
  }
  return [...unique];
}
